import { Controller } from "@hotwired/stimulus"

/**
 * Controller for managing RTQ Disability Claims table with add/delete rows and auto-save.
 *
 * Usage:
 *   <div data-controller="rtq-disability-claims"
 *        data-rtq-disability-claims-create-url-value="/path/to/create">
 */
export default class extends Controller {
  static targets = ["row", "addButton", "status"]

  static values = {
    createUrl: String,
    debounce: { type: Number, default: 500 }
  }

  connect() {
    this.debounceTimers = {}
  }

  disconnect() {
    Object.values(this.debounceTimers).forEach(timer => clearTimeout(timer))
  }

  async addRow(event) {
    event.preventDefault()

    this.showStatus("Adding...")

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ rtq_disability_claim: {} })
      })

      const data = await response.json()

      if (response.ok && data.status === "success") {
        // Reload the page to show the new row
        window.location.reload()
      } else {
        this.showStatus("Error adding", true)
      }
    } catch (error) {
      console.error("Error adding disability claim:", error)
      this.showStatus("Network error", true)
    }
  }

  async deleteRow(event) {
    event.preventDefault()

    if (!confirm("Are you sure you want to delete this disability claim?")) return

    const button = event.currentTarget
    const url = button.dataset.url

    this.showStatus("Deleting...")

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        }
      })

      const data = await response.json()

      if (response.ok && data.status === "success") {
        window.location.reload()
      } else {
        this.showStatus("Error deleting", true)
      }
    } catch (error) {
      console.error("Error deleting disability claim:", error)
      this.showStatus("Network error", true)
    }
  }

  fieldChanged(event) {
    const input = event.target
    const url = input.dataset.url
    const field = input.dataset.field

    if (!url || !field) return

    // Clear existing timer for this input
    const timerId = `${url}-${field}`
    if (this.debounceTimers[timerId]) {
      clearTimeout(this.debounceTimers[timerId])
    }

    this.showStatus("Saving...")

    this.debounceTimers[timerId] = setTimeout(() => {
      this.saveField(url, field, input.value)
    }, this.debounceValue)
  }

  checkboxChanged(event) {
    const input = event.target
    const url = input.dataset.url
    const field = input.dataset.field

    if (!url || !field) return

    this.showStatus("Saving...")
    this.saveField(url, field, input.checked)
  }

  async saveField(url, field, value) {
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ rtq_disability_claim: { [field]: value } })
      })

      const data = await response.json()

      if (response.ok && data.status === "success") {
        this.showStatus("Saved", false, 2500)
      } else {
        this.showStatus("Error saving", true)
      }
    } catch (error) {
      console.error("Error saving field:", error)
      this.showStatus("Network error", true)
    }
  }

  showStatus(message, isError = false, autoClearMs = 0) {
    if (!this.hasStatusTarget) return

    const statusClass = isError ? "text-red-600" : "text-gray-400"
    this.statusTarget.innerHTML = `<span class="${statusClass}">${message}</span>`

    if (autoClearMs > 0) {
      setTimeout(() => {
        this.statusTarget.innerHTML = ""
      }, autoClearMs)
    }
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
